import { Snowflake, Flame, AirVent, Building2, Siren, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";

const GROUPS = [
  { key: "cooling", icon: Snowflake, title: "Cooling", items: [
    { key: "ac-repair", label: "AC Repair", desc: "Same-day diagnostics & repair." },
    { key: "ac-install", label: "AC Installation", desc: "Premium installs, sized right." },
    { key: "ac-replace", label: "AC Replacement", desc: "Upgrade aging systems efficiently." },
  ]},
  { key: "heating", icon: Flame, title: "Heating", items: [
    { key: "furnace-repair", label: "Furnace Repair", desc: "Heat restored fast — any brand." },
    { key: "furnace-install", label: "Furnace Installation", desc: "Quiet, efficient modern installs." },
  ]},
  { key: "additional", icon: AirVent, title: "Additional Services", items: [
    { key: "mini-split", label: "Mini Splits", desc: "Ductless comfort & zoning." },
    { key: "commercial", label: "Commercial HVAC", desc: "Rooftops & light commercial." },
    { key: "emergency", label: "Emergency HVAC Service", desc: "24/7 emergency response.", emphasis: true },
  ]},
];

export default function Services() {
  const { t } = useLang();
  return (
    <section id="services" className="py-12 md:py-20 bg-ink-800/30" data-testid="services">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-ice-400">Service Directory</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-black">{t("services.title")}</h2>
            <p className="mt-2 text-silver-300 max-w-xl text-sm md:text-base">Find a service in seconds.</p>
          </div>
          <Link to="/schedule" className="btn-primary px-5 py-3 rounded-full text-sm" data-testid="services-cta">{t("cta.schedule")}</Link>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {GROUPS.map((g, gi) => {
            const Icon = g.icon;
            return (
              <div key={g.key} className="card-ink rounded-2xl p-5" data-testid={`svc-group-${g.key}`}>
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-xl bg-ice-400/15 ring-1 ring-ice-400/30 grid place-items-center"><Icon className="size-5 text-ice-400"/></div>
                  <h3 className="font-display text-lg font-bold">{g.title}</h3>
                </div>
                {/* Desktop: list. Mobile: accordion */}
                <div className="hidden md:block mt-3">
                  <ul className="grid">
                    {g.items.map(it => (
                      <li key={it.key}>
                        <Link to="/schedule" className={`flex items-start gap-2 py-2.5 group ${it.emphasis ? "text-red-300" : ""}`} data-testid={`svc-${it.key}`}>
                          <ChevronRight className="size-4 mt-0.5 text-silver-500 group-hover:text-ice-400 transition" />
                          <div>
                            <div className="font-semibold text-sm">{it.label}</div>
                            <div className="text-xs text-silver-400">{it.desc}</div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="md:hidden mt-2">
                  <Accordion type="single" collapsible>
                    {g.items.map(it => (
                      <AccordionItem key={it.key} value={it.key} className="border-white/5">
                        <AccordionTrigger className={`text-left text-sm font-semibold hover:no-underline ${it.emphasis ? "text-red-300" : ""}`} data-testid={`svc-m-${it.key}`}>{it.label}</AccordionTrigger>
                        <AccordionContent className="text-silver-300 text-sm">
                          <p>{it.desc}</p>
                          <Link to="/schedule" className="mt-2 inline-flex btn-primary rounded-full px-3 py-1.5 text-xs">Schedule</Link>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
